import { useEffect, useRef, useState, cloneElement } from "react";
/**
 * @export
 * A component that applies enter/exit transition animations to a single child element.
 *
 * @example
 * ```
 * <Transition in={true} duration={300} className="fade">
 *   <div>Content</div>
 * </Transition>
 * ```
 *
 * @example
 * ```
 * <Transition
 *   in={false}
 *   duration={500}
 *   className="slide"
 *   unmountOnExit={true}
 * >
 *   <div style={{ background: '#f0f0f0', padding: '10px' }}>
 *     Animated Content
 *   </div>
 * </Transition>
 * ```
 */
const Transition = ({ children, className, in: inProp, duration, unmountOnExit = false, }) => {
    // const nodeRef = useRef<HTMLElement>(null);
    const [status, setStatus] = useState(inProp ? "entered" : "exited");
    const [mounted, setMounted] = useState(inProp || !unmountOnExit);
    // Track previous prop to detect changes
    const prevInProp = useRef(inProp);
    // Store timeouts to clear them properly
    const timeoutRef = useRef(null);
    useEffect(() => {
        // Only run effect if 'in' prop actually changed
        if (prevInProp.current !== inProp) {
            prevInProp.current = inProp;
            // Clear any existing timeouts to prevent race conditions
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (inProp) {
                // First make sure we're mounted
                setMounted(true);
                // Then use RAF to ensure DOM is updated before animation starts
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // First set to 'enter' which applies initial styles
                        setStatus("enter");
                        // Use RAF again to ensure those styles are applied
                        requestAnimationFrame(() => {
                            // Then apply active state for animation to start
                            setStatus("entering");
                            // After duration, set to final state
                            timeoutRef.current = setTimeout(() => {
                                setStatus("entered");
                            }, duration);
                        });
                    });
                });
            }
            else {
                // Handle exit animation
                setStatus("exit");
                // Use RAF to ensure styles are applied
                requestAnimationFrame(() => {
                    setStatus("exiting");
                    // After duration, handle final state and potential unmount
                    timeoutRef.current = setTimeout(() => {
                        setStatus("exited");
                        if (unmountOnExit) {
                            setMounted(false);
                        }
                    }, duration);
                });
            }
        }
        // Clean up on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [inProp, duration, unmountOnExit]);
    if (!mounted)
        return null;
    // Map the status to the appropriate classes
    let transitionClass = "";
    switch (status) {
        case "enter":
            transitionClass = `${className}-enter`;
            break;
        case "entering":
            transitionClass = `${className}-enter ${className}-enter-active`;
            break;
        case "entered":
            transitionClass = `${className}-enter-done`;
            break;
        case "exit":
            transitionClass = `${className}-exit`;
            break;
        case "exiting":
            transitionClass = `${className}-exit ${className}-exit-active`;
            break;
        case "exited":
            transitionClass = `${className}-exit-done`;
            break;
    }
    return cloneElement(children, {
        // ref: nodeRef as any,
        className: `${children.props.className || ""} ${transitionClass}`.trim(),
        style: Object.assign(Object.assign({}, children.props.style), { transitionDuration: `${duration}ms` }),
    });
};
export default Transition;
